import type { ToastStore } from '@skeletonlabs/skeleton'
import { events } from 'fetch-event-stream'
import snakecaseKeys from 'snakecase-keys'

import { airGappedUserPasswordLocalStorage, forceLogout } from './auth'
import { getConfig } from './config.svelte'

let lastCsrfToken = ''
let isAirGapped: boolean
let airGappedUserPassword: string

const HIRUNDO_API_VERSION = '0.3'
type FetchOptions<T extends string> = Omit<RequestInit, 'body'> & {
  allowErrorResponse?: boolean
  body?: Partial<Record<T, unknown>>
}
type SseOptions<T extends string> = Omit<FetchOptions<T>, 'allowErrorResponse'>

type FailedResponseInspection = {
  csrfMismatch: boolean
  failureMessage: string
}

const setCachedAirGappedUserPassword = (encodedPassword: string) => {
  airGappedUserPassword = encodedPassword
}

const getAirGappedUserPassword = () => {
  airGappedUserPassword = localStorage.getItem(airGappedUserPasswordLocalStorage) ?? ''
  return airGappedUserPassword
}

const resolveHost = async (host: string | undefined, toastStore?: ToastStore) => {
  if (host !== undefined) {
    return host
  }
  const config = await getConfig(toastStore)
  if (isAirGapped === undefined) {
    isAirGapped = config.airGapped ?? false
    getAirGappedUserPassword()
  }
  return config.apiBaseUrl
}

const requestUsesCsrf = (method: string | undefined) =>
  ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method ?? '')

const ensureCsrfToken = async (host: string, useCsrf: boolean) => {
  if (!useCsrf || lastCsrfToken !== '') {
    return
  }
  // ⬆️ Get a new CSRF token if we don't have one yet
  lastCsrfToken =
    (
      await fetch(`${host}/csrf`, {
        credentials: 'include',
        headers: {
          'HIRUNDO-API-VERSION': HIRUNDO_API_VERSION,
        },
      })
    ).headers.get('X-CSRF-Token') || ''
}

const buildRequestInit = <T extends string>(options: FetchOptions<T>, useCsrf: boolean) => {
  const { allowErrorResponse: _allowErrorResponse, ...requestOptions } = options
  return {
    credentials: 'include' as RequestCredentials,
    ...requestOptions,
    body: options.body ? JSON.stringify(snakecaseKeys(options.body)) : undefined,
    headers: {
      'HIRUNDO-API-VERSION': HIRUNDO_API_VERSION,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
      ...(useCsrf ? { 'X-CSRF-Token': lastCsrfToken } : {}),
      ...(isAirGapped ? { Authorization: `Basic ${getAirGappedUserPassword()}` } : {}),
    },
  }
}

const prepareAndFetch = async <T extends string>(
  path: string,
  options: FetchOptions<T>,
  host: string | undefined,
  toastStore?: ToastStore,
) => {
  const resolvedHost = await resolveHost(host, toastStore)
  const useCsrf = requestUsesCsrf(options.method)
  await ensureCsrfToken(resolvedHost, useCsrf)
  return {
    response: await fetch(`${resolvedHost}${path}`, buildRequestInit(options, useCsrf)),
    host: resolvedHost,
    useCsrf,
  }
}

class FetchError extends Error {
  constructor(
    public statusCode: number,
    public reason: string,
  ) {
    super(reason)
    this.statusCode = statusCode
    this.name = 'FetchError'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const formatValidationDetails = (details: unknown[]) =>
  details
    .map((detail) => {
      if (!isRecord(detail) || typeof detail.msg !== 'string') {
        return ''
      }
      if (!Array.isArray(detail.loc) || detail.loc.length <= 1) {
        return detail.msg
      }
      const location = detail.loc
        .slice(1)
        .filter((item): item is string => typeof item === 'string')
        .map((item) =>
          item
            .replaceAll('_', ' ')
            .replace(/tagged-union\[(.*?)\]/, (_, args) => args.split(',').join(' or '))
            .replace(/list\[(.*?)\]/, (_, args) => `list of ${args}`),
        )
        .join(' ➡️ ')
      return location ? `${detail.msg}: ${location}` : detail.msg
    })
    .filter(Boolean)
    .join('; ')

const getJsonErrorMessage = (responseJson: unknown) => {
  if (!isRecord(responseJson)) {
    return ''
  }
  if (typeof responseJson.reason === 'string' && responseJson.reason) {
    return responseJson.reason
  }
  if (Array.isArray(responseJson.detail)) {
    return formatValidationDetails(responseJson.detail)
  }
  if (typeof responseJson.detail === 'string' && responseJson.detail) {
    return responseJson.detail
  }
  return typeof responseJson.message === 'string' ? responseJson.message : ''
}

const getResponseErrorMessage = async (response: Response) => {
  const jsonResponse = response.clone()
  try {
    const failureMessage = getJsonErrorMessage(await jsonResponse.json())
    if (failureMessage) {
      return failureMessage
    }
  } catch {
    // Fall back to the text response below.
  }
  return (await response.clone().text()) || response.statusText
}

const ensureResponseOk = async (response: Response) => {
  if (!response.ok) {
    throw new FetchError(response.status, await getResponseErrorMessage(response))
  }
}

const inspectFailedResponse = async (
  response: Response,
  resolvedHost: string,
  path: string,
): Promise<FailedResponseInspection> => {
  const textResponse = response.clone()
  let responseJson: unknown
  let failureMessage = ''
  try {
    responseJson = await response.json()
    failureMessage = getJsonErrorMessage(responseJson)
  } catch (error) {
    console.error(
      "Couldn't parse response JSON",
      resolvedHost,
      path,
      error,
      'Trying to parse as text',
    )
    try {
      failureMessage = await textResponse.text()
    } catch (textError) {
      console.error('Request failed!', resolvedHost, path, textError)
    }
  }

  const responseMessage = isRecord(responseJson) ? responseJson.message : undefined
  return {
    csrfMismatch:
      responseMessage === 'The CSRF signatures submitted do not match.' ||
      responseMessage === 'Missing Cookie: `fastapi-csrf-token`.',
    failureMessage: failureMessage || response.statusText,
  }
}

const handleFailedResponse = async (
  response: Response,
  resolvedHost: string,
  path: string,
  useCsrf: boolean,
  returnResponseOnFailure = false,
): Promise<boolean> => {
  const failure = await inspectFailedResponse(
    returnResponseOnFailure ? response.clone() : response,
    resolvedHost,
    path,
  )
  if (useCsrf && failure.csrfMismatch) {
    lastCsrfToken = ''
    return true
  }
  if (response.status === 401) {
    console.error('Request returned 401, logging user out', resolvedHost, path)
    await forceLogout()
    throw new FetchError(response.status, 'Unauthorized')
  }
  if (returnResponseOnFailure) {
    return false
  }
  throw new FetchError(response.status, failure.failureMessage)
}

const handleSse = async function* <T extends string>(
  path: string,
  options: SseOptions<T>,
  host?: string,
  toastStore?: ToastStore,
  retry = 0,
): AsyncGenerator<Response | undefined> {
  if (retry > 3) {
    throw new FetchError(500, 'Failed to fetch data. Exceeded retry limit.')
  }
  const {
    response,
    host: resolvedHost,
    useCsrf,
  } = await prepareAndFetch(path, options, host, toastStore)
  if (!response.ok) {
    if (await handleFailedResponse(response, resolvedHost, path, useCsrf)) {
      yield* handleSse(path, options, host, toastStore, retry + 1)
      return
    }
  }
  const eventSource = events(response, options.signal)
  for await (const event of eventSource) {
    yield new Response(event.data)
  }
}

async function handleFetch<T extends string>(
  path: string,
  options: FetchOptions<T> = {},
  host?: string,
  toastStore?: ToastStore,
  retry = 0,
  sse = false,
): Promise<Response> {
  if (retry > 3) {
    throw new FetchError(500, 'Failed to fetch data. Exceeded retry limit.')
  }
  const {
    response,
    host: resolvedHost,
    useCsrf,
  } = await prepareAndFetch(path, options, host, toastStore)
  if (sse && response.ok) {
    const eventSource = events(response, options.signal)
    for await (const event of eventSource) {
      return new Response(event.data)
    }
  }
  if (response.headers.has('X-CSRF-Token')) {
    // ⬆️ If the response has a new CSRF token, save it. This uses existing GET requests to store a CSRF
    lastCsrfToken = response.headers.get('X-CSRF-Token') || ''
  }
  if (useCsrf) {
    // ⬆️ If we used the CSRF token, clear it. It is no longer valid
    lastCsrfToken = ''
  }
  if (toastStore && response.headers.has('X-License-Status')) {
    let productString = ''
    let expiryString = ''
    if (response.headers.has('X-License-Product')) {
      productString = ` for ${response.headers.get('X-License-Product')} `
    }
    if (response.headers.has('X-License-Expires-In')) {
      expiryString = ` Your license expires in ${response.headers.get('X-License-Expires-In')}. `
    }
    if (response.headers.get('X-License-Status') === 'EXPIRED') {
      toastStore.trigger({
        message: `Your license ${productString}has expired. Please contact Hirundo to renew your license. Until you do so, no new runs can be started.`,
        background: 'variant-filled-error',
      })
    } else if (response.headers.get('X-License-Status') === 'INVALID') {
      toastStore.trigger({
        message: `Your license ${productString}is invalid. Please contact Hirundo to obtain a valid license. Until you do so, no new runs can be started.`,
        background: 'variant-filled-error',
      })
    } else if (response.headers.get('X-License-Status') === 'EXPIRES_SOON') {
      toastStore.trigger({
        message: `Your license ${productString}will expire in the next 60 days.${expiryString} Please contact Hirundo to renew your license.`,
        background: 'variant-filled-warning',
      })
    }
  }
  if (!response.ok) {
    if (
      await handleFailedResponse(response, resolvedHost, path, useCsrf, options.allowErrorResponse)
    ) {
      console.error('CSRF token mismatch, reloading')
      return handleFetch(path, options, host, toastStore, retry + 1)
    }
  }
  return response
}

export {
  ensureResponseOk,
  FetchError,
  getResponseErrorMessage,
  handleFetch,
  handleSse,
  setCachedAirGappedUserPassword,
}
